import { isAfter, subHours } from 'date-fns';

import { useIsFocused } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { ignoreTimezone } from '@order/utils/src/date';

import { useHomePendingFeedbackOrderQuery } from './graphql/HomePendingFeedbackOrder.generated';

/**
 * Returns the order for this order id if it is pending feedback.
 * Additionally ignores it if the order wasn't completed over an hour ago.
 */
export const useHomePendingFeedbackOrder = (params: UseHomePendingFeedbackOrderParams) => {
  const { orderId = '', isLoggedIn } = params;

  const isFocused = useIsFocused();

  const [response] = useHomePendingFeedbackOrderQuery({
    variables: { orderId },
    pause: !orderId || !isFocused || !isLoggedIn,
    requestPolicy: 'network-only',
  });

  const rateableOrder = getOrderPendingFeedback(response);

  return {
    rateableOrder,
    isLoadingFeedback: response.fetching,
  };
};

function getOrderPendingFeedback(response: ReturnType<typeof useHomePendingFeedbackOrderQuery>[0]) {
  const { data } = response;

  if (data?.orderStatus?.__typename !== 'OrderStatus' || !data?.orderStatus?.order) {
    return null;
  }

  const order = data.orderStatus.order;

  if (!order.isPendingFeedback) return null;

  const orderStatus = data.orderStatus.flattenedOrderStatus;

  if (orderStatus !== FlattenedOrderStatuses.Completed) return null;

  const orderTime = ignoreTimezone(order.wantedTime);

  if (!orderTime) return null;

  const oneHourAgo = subHours(new Date(), 1);

  if (isAfter(orderTime, oneHourAgo)) return null;

  return order;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseHomePendingFeedbackOrderParams = {
  orderId?: string;
  isLoggedIn: boolean;
};
